import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom, take } from 'rxjs';

import { ChannelCategory } from 'icreate/common/models/channel-category';
import { Vessel } from 'icreate/common/models/vessel';
import { AlarmGroupHelperService } from '@service/alarm-group-helper.service';
import { AlarmGroupEditDialogComponent } from '../alarm-group-edit-dialog/alarm-group-edit-dialog.component';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogOption,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'app-alarm-group-row',
    templateUrl: './alarm-group-row.component.html',
    styleUrls: ['./alarm-group-row.component.css'],
    standalone: false,
})
export class AlarmRowComponent implements OnChanges {
    @Input() alarmGroup: any;
    @Input() alarmGroupEditMode = false;
    @Input() userCanEdit: boolean = true;
    @Input() index: number;
    @Input() vessel!: Vessel;

    @Output() onDuplicateRow = new EventEmitter<any>();
    @Output() onDeleteRow = new EventEmitter<void>();
    @Output() onRowUpdated = new EventEmitter<boolean>();

    @ViewChild('optionsIcon')
    optionsIcon: ElementRef;

    category: ChannelCategory[];
    optionsVisible: boolean;
    isInfoMessageVisible = false;
    isRowActionActive: boolean = false;

    constructor(
        private translate: TranslateService,
        private alarmGroupHelperService: AlarmGroupHelperService,
        private dialog: MatDialog
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.alarmGroup) {
            this.category = this.alarmGroup.categories?.length ? this.alarmGroup.categories : null;
        }
    }

    ngOnInit() {
        this.category = this.alarmGroup.categories?.length ? this.alarmGroup.categories : null;
    }

    ngAfterViewInit() {
        document.addEventListener(
            'scroll',
            () => {
                if (this.optionsVisible) {
                    this.optionsVisible = false;
                }
            },
            true
        );
    }

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (!this.optionsIcon?.nativeElement.contains(event.target) && this.optionsVisible) {
            this.optionsVisible = false;
        }
    }

    private openRowDialog<T, R>(dialogComponent: ComponentType<T>, config?: MatDialogConfig): MatDialogRef<T, R> {
        this.isRowActionActive = true;
        const dialogRef = this.dialog.open(dialogComponent, config);
        dialogRef.afterClosed().subscribe(() => {
            this.isRowActionActive = false;
        });
        return dialogRef;
    }

    getName() {
        const message = this.translate.instant('alarm-group.page.no-name-message');
        return this.alarmGroup.name ? this.alarmGroup.name : message;
    }

    getDescription() {
        const message = this.translate.instant('alarm-group.page.no-description-message');
        return this.alarmGroup.description ? this.alarmGroup.description : message;
    }

    getCategoryName() {
        return this.category && this.category.length > 0
            ? this.category
                  .slice()
                  .sort((a, b) => a.category.localeCompare(b.category))
                  .map(c => c.category)
                  .join(', ')
            : null;
    }

    async editAlarmGroup() {
        const dialogRef = this.openRowDialog<AlarmGroupEditDialogComponent, { alarmGroup: any }>(
            AlarmGroupEditDialogComponent,
            {
                data: {
                    ...this.alarmGroup,
                    vesselId: this.vessel.id,
                    categories: this.category,
                },
                disableClose: true,
            }
        );

        dialogRef.afterClosed().subscribe(result => {
            if (result?.alarmGroup) {
                Object.assign(this.alarmGroup, result.alarmGroup);
                this.category = result.alarmGroup.categories?.length > 0 ? result.alarmGroup.categories : null;
                this.alarmGroup.categoriesId = result.alarmGroup.categoriesId;

                this.rowUpdated();
            }
        });
    }

    async resetTriggerCategory() {
        const hasMultipleCategories = (this.category?.length ?? 0) > 1;
        const messageKey = hasMultipleCategories
            ? 'alarm-group.page.exit-confirmation.title_plural'
            : 'alarm-group.page.exit-confirmation.title';

        const dialogRef = this.openRowDialog<ConfirmationDialogComponent, ConfirmationDialogOption>(
            ConfirmationDialogComponent,
            {
                width: '443px',
                data: {
                    type: ConfirmationDialogType.SIMPLE,
                    message: messageKey,
                    discardButton: 'alarm-group.page.exit-confirmation.cancel',
                    saveButton: 'alarm-group.page.exit-confirmation.confirm',
                    with: '443px',
                },
            }
        );

        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) {
            this.category = null;
            this.alarmGroup.categoryId = null;
            this.alarmGroup.categories = null;
            this.rowUpdated();
        }
    }

    openOptions() {
        this.optionsVisible = !this.optionsVisible;
    }

    duplicateRow() {
        this.onDuplicateRow.emit();
        this.optionsVisible = false;
    }

    deleteRow() {
        this.onDeleteRow.emit();
        this.optionsVisible = false;
    }

    rowUpdated() {
        this.alarmGroupHelperService.groupUpdated();
    }
}
